import { MenuItem, Position } from "@blueprintjs/core"
import { MultiSelect } from "@blueprintjs/select"
import { get } from "lodash-es"
import React, { useState } from "react"
import classNames from "utils/classNames"
import { withFormController } from "../Molecules/FormController"
import CustomButton from "./CustomButton"
import CustomFormGroup from "./CustomFormGroup"
import CustomTooltip from "./CustomTooltip"

const StaticMultiSelector = ({
  absolute,
  inline,
  loading,
  className,
  formClassName,
  items,
  path,
  value,
  onChange,
  onDelete = undefined,
  onCreate,
  renderText,
  renderLabel,
  renderTag,
  label,
  searchBy,
  helperText,
  error,
  labelInfo,
  placeholder,
  formGroupProps,
  fill,
  big,
  scrollToActiveItem,
  disabled,
  renderDisabled,
  tagProps,
  newItem,
  search,
  popoverClassName,
  onSearchChange,
  itemPredicate,
  setSearch,
  isServerSide,
}) => {
  const [query, changeQuery] = useState("")
  const [selectAll, changeSelectAll] = useState(true)

  const isItemSelected = item => {
    // eslint-disable-next-line
    return value.find(selectedItem => selectedItem?.id == item?.id)
  }
  const renderItem = (item, { handleClick, modifiers }) => {
    return (
      <MenuItem
        disabled={renderDisabled(item)}
        key={item.id}
        active={modifiers.active}
        onClick={handleClick}
        text={renderText(item)}
        icon={isItemSelected(item) ? "endorsed" : "blank"}
        labelElement={renderLabel && renderLabel(item)}
      />
    )
  }
  const filterItem = (query, item) => {
    const searchQuery = query.toLowerCase()
    let flag = false
    searchBy.forEach(searchParam => {
      const value = get(item, searchParam)?.toLowerCase()
      if (value?.includes(searchQuery)) return (flag = true)
    })

    return query ? flag && item : item
  }
  const handleSelect = item => {
    if (typeof item === "string") {
      onChange(path, value?.concat(newItem(item)))
    } else if (isItemSelected(item)) {
      onChange(
        path,
        value.filter(itm => itm.id !== item.id)
      )
    } else {
      onChange(path, value.concat(item))
    }
    onSearchChange ? onSearchChange("") : changeQuery("")
  }
  const handleRemove = (_, index) => {
    onChange(
      path,
      value.filter((_, idx) => idx !== index)
    )
    onDelete?.(value?.[index], index)
  }

  const createNewItemRenderer = (query, active, handleClick) => {
    return (
      <MenuItem
        icon="add"
        text={`Create "${query}"`}
        active={active}
        onClick={handleClick}
        shouldDismissPopover={false}
      />
    )
  }
  const createNewItemFromQuery = query => query
  function handleQueryChange(query) {
    if (isServerSide) {
      setSearch(query)
    }
    onSearchChange ? onSearchChange(query) : changeQuery(query)
  }

  function RightButton({ list }) {
    return (
      <CustomTooltip
        usePortal
        placement="top"
        disabled={disabled}
        content={selectAll ? "Select all items" : "Clear all items"}
      >
        {selectAll ? (
          <CustomButton
            disabled={disabled || loading}
            className={big ? "mt-1" : ""}
            onClick={() => {
              if (query) {
                const filtered = list.filter(item => filterItem(query, item))
                onChange(path, filtered)
              } else {
                onChange(path, list)
              }
              changeSelectAll(false)
            }}
            minimal
            icon="plus"
            intent="success"
          />
        ) : (
          <CustomButton
            disabled={disabled || loading}
            className={big ? "mt-1" : ""}
            onClick={() => {
              onChange(path, [])
              changeSelectAll(true)
            }}
            minimal
            icon="cross"
            intent="danger"
          />
        )}
      </CustomTooltip>
    )
  }

  return (
    <CustomFormGroup
      className={classNames({ "vt-selector-input-big": big }, formClassName)}
      label={label}
      helperText={helperText}
      error={error}
      labelInfo={labelInfo}
      big={big}
      inline={inline}
      {...formGroupProps}
    >
      <MultiSelect
        query={search ?? query}
        onQueryChange={onSearchChange ?? handleQueryChange}
        className={classNames(
          {
            "vt-static-multiselector-loading": loading,
          },
          className
        )}
        popoverProps={{
          minimal: true,
          position: Position.BOTTOM_LEFT,
          popoverClassName: classNames(
            {
              absolute: absolute,
            },
            popoverClassName
          ),
        }}
        tagInputProps={{
          className: big ? "vt-multiselector-tag-input-big" : "",
          rightElement: <RightButton list={items} />,
          disabled: disabled || loading,
          placeholder: loading ? "Loading.." : placeholder,
          onRemove: handleRemove,
          tagProps,
        }}
        resetOnSelect={false}
        itemPredicate={itemPredicate ? itemPredicate : filterItem}
        items={items || []}
        itemRenderer={renderItem}
        itemsEqual="id"
        tagRenderer={renderTag}
        onItemSelect={handleSelect}
        selectedItems={value}
        scrollToActiveItem={scrollToActiveItem}
        createNewItemRenderer={onCreate ? createNewItemRenderer : undefined}
        createNewItemFromQuery={onCreate ? createNewItemFromQuery : undefined}
        noResults={<MenuItem key="empty" disabled={true} text="No results." />}
        fill={fill}
      />
    </CustomFormGroup>
  )
}

StaticMultiSelector.defaultProps = {
  renderTag(item) {
    return item && item.name
  },
  renderDisabled(item) {
    return false
  },
  newItem: item => ({ id: item, name: item }),
  loading: false,
  searchBy: ["name", "address.taluka"],
  value: [],
  renderText: item => item && item.name,
  scrollToActiveItem: true,
  tagProps: {
    minimal: true,
  },
  isServerSide: false,
}
export default withFormController(StaticMultiSelector)
